import type { CameraData, Model3D, NodeFile } from '@/entities/model3d'
import { uniq } from 'ramda'
import type { LoaderConfig, ModelType } from '@/lib/three-d-resources/model-loader'
import { loadModelResource, clearCache } from '@/lib/three-d-resources/model-loader'
import { Model } from '@/lib/three-d-resources/model'
import type { SceneConfig } from '@/lib/three-d-resources/scene-config'
import { CameraDataSceneConfig, DefaultSceneConfig } from '@/lib/three-d-resources/scene-config'
import type { TargetVisualizer } from './target-visualizer'

export class ThreeDResourceManager {
  sceneConfigs: Map<number, SceneConfig> = new Map()
  private _models: Map<number, Model> = new Map()

  constructor(
    private loaderConfig: LoaderConfig,
    private targetVisualizer: TargetVisualizer | null,
    private onLayerComplete: (params: { modelId: number; layerId: number | null; cameraId?: number }) => void,
    private onUpdateProgress: (modelId: number, sceneId: number | null, newProgress: number) => void,
    private onSceneComplete: (params: { cameraId?: number }) => void,
    private onError?: (error: Error) => void,
  ) {}

  async loadPriorityList(cameras: readonly CameraData[]) {
    const modelIds = uniq(cameras.map(c => c.model3d_id))
    const unRequestedModelIds = modelIds.filter(id => !this._models.has(id))
    await this.loadModels(unRequestedModelIds)

    for (const camera of cameras) {
      if (!this.sceneConfigs.has(camera.id)) {
        this.sceneConfigs.set(camera.id, new CameraDataSceneConfig(this._models.get(camera.model3d_id)!, camera))
      }
    }

    await this.getResourcesForCameras(cameras)
  }

  async loadResourcesForModelId(modelId: number) {
    const model = this._models.get(modelId)
    if (!model) return
    const files = model.getFilesToDownload(() => true)

    for (const nodeFile of files) {
      const layer = await this.loadFileForModel({ nodeFile, model })

      for (const config of this.sceneConfigs.values()) {
        const cameraData = model.apiData.cameras.find(c => c.id === config.id)
        if (cameraData) config.updateVisibleNodeIdsForV1Models()
      }

      this.onUpdateProgress(nodeFile.model3d_id, nodeFile.node_id, 1)

      this.onLayerComplete({
        modelId: layer.modelId,
        layerId: layer.nodeId,
      })

      this.onSceneComplete({})
    }
  }

  createNewSceneConfig(camera: CameraData): CameraDataSceneConfig {
    const model = this._models.get(camera.model3d_id)

    if (!model) throw new Error(`Model ${camera.model3d_id} does not exist`)

    const sceneConfig = new CameraDataSceneConfig(model, camera)
    this.sceneConfigs.set(camera.id, sceneConfig)

    return sceneConfig
  }

  resetResources() {
    this._models = new Map()
    this.sceneConfigs = new Map()
    clearCache()
  }

  async getModel(modelId: number): Promise<Model> {
    const model = this._models.get(modelId)
    if (model) return model

    await this.loadModels([modelId])
    return this._models.get(modelId)!
  }

  async getSceneConfig(model: Model, cameraId: number | null): Promise<SceneConfig> {
    if (cameraId === null) return new DefaultSceneConfig(model)

    const sceneConfig = this.sceneConfigs.get(cameraId)
    if (sceneConfig) return sceneConfig

    const data = await $fetch<CameraData>(`v2/3dmodel/${model.id}/cameras/${cameraId}`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
      params: { access_type: this.loaderConfig.accessType ?? undefined },
    })

    // During the time that we were awaiting this could've been set now
    const secondCheckSceneConfig = this.sceneConfigs.get(cameraId)
    if (secondCheckSceneConfig) return secondCheckSceneConfig

    const newConfig = new CameraDataSceneConfig(model, data)
    this.sceneConfigs.set(cameraId, newConfig)
    return newConfig
  }

  private async loadModels(modelIds: number[]) {
    const res = await Promise.all(
      modelIds.map(id =>
        $fetch<Model3D>(`v2/3dmodel/${id}`, {
          ...useFetchSharedOptions(),
          headers: {
            ...useFetchSharedOptions().headers,
            'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
          },
          params: { access_type: this.loaderConfig.accessType ?? undefined },
        }),
      ),
    )

    for (const apiModel of res) {
      const resourcesManager = new Model(apiModel)
      if (!this._models.has(apiModel.id)) {
        this._models.set(apiModel.id, resourcesManager)
      }
    }
  }

  hasV1Models(): boolean {
    return Array.from(this._models.values()).some(model => model.apiData.version === 1)
  }

  private async getResourcesForCameras(cameras: readonly CameraData[]) {
    const visibleNodesFiles = cameras.map(c => this._models.get(c.model3d_id)!.getVisibleNodesFiles(c))

    this.setInitialVisibleNodeFilesProgress(visibleNodesFiles)

    for (const [index, cameraFiles] of visibleNodesFiles.entries()) {
      if (!cameraFiles.length) continue

      const model = this._models.get(cameraFiles[0].model3d_id)
      const camera = cameras[index]

      await Promise.all(
        cameraFiles.map(
          file =>
            new Promise<void>(async resolve => {
              const modelsHaveBeenCleared = !model
              if (modelsHaveBeenCleared) return

              try {
                const layer = await this.loadFileForModel({ nodeFile: file, model })
                this.sceneConfigs.get(camera.id)?.updateVisibleNodeIdsForV1Models()

                this.onUpdateProgress(file.model3d_id, file.node_id, 1)

                this.onLayerComplete({
                  modelId: layer.modelId,
                  layerId: layer.nodeId,
                  cameraId: camera.id,
                })
              } catch (e: unknown) {
                this.onError?.(e as Error)
              }

              resolve()
            }),
        ),
      )
      this.onSceneComplete({ cameraId: camera.id })
    }

    const hiddenNodesFiles = cameras.flatMap(c => this._models.get(c.model3d_id)?.getHiddenNodesFiles(c) ?? [])

    for (const nodeFile of hiddenNodesFiles) {
      const modelResourceManager = this._models.get(nodeFile.model3d_id)

      const modelsHaveBeenCleared = !modelResourceManager
      if (modelsHaveBeenCleared) return

      const layer = await this.loadFileForModel({ nodeFile, model: modelResourceManager })

      this.onUpdateProgress(nodeFile.model3d_id, nodeFile.node_id, 1)

      this.onLayerComplete({
        modelId: layer.modelId,
        layerId: layer.nodeId,
      })
    }
  }

  private setInitialVisibleNodeFilesProgress(visibleNodesFiles: NodeFile[][]) {
    for (const cameraFiles of visibleNodesFiles) {
      for (const file of cameraFiles) {
        this.onUpdateProgress(file.model3d_id, file.node_id, 0.01)
      }
    }
  }

  private async loadFileForModel({ nodeFile, model }: { nodeFile: NodeFile; model: Model }): Promise<ModelType> {
    const alreadyExistingFile = model.modelNodes.find(({ url }) => url === nodeFile.file.url)

    if (alreadyExistingFile) {
      return alreadyExistingFile
    }

    let layer = {} as ModelType

    try {
      layer = await loadModelResource(
        nodeFile,
        model.apiData,
        event => {
          // Set slightly under 1 so final updateProgress signals download is done
          this.onUpdateProgress(nodeFile.model3d_id, nodeFile.node_id, event.loaded / event.total - 0.01)
        },
        this.loaderConfig,
      )

      await model.addResource(layer)
      this.targetVisualizer?.addNode(layer.group)
    } catch (e: unknown) {
      this.onError?.(e as Error)
    }
    return layer
  }
}
